import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import marketService from '../services/marketService';
import useAuth from '../hooks/useAuth';
import './MarketForm.css';

const MarketEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: '',
    age: '',
    city: '',
    phone: '',
    position: '',
    experience: '',
    description: '',
    videoLinks: '', // Renomeado de 'videos' para 'videoLinks' para clareza
    photos: [], // novos arquivos de imagem
    existingPhotos: [] // URLs das fotos já salvas
  });

  // --- Novos estados para gerenciar uploads de vídeo ---
  const [videoFiles, setVideoFiles] = useState([]); // Armazena os arquivos de vídeo para upload
  const [existingVideos, setExistingVideos] = useState([]); // Armazena URLs de vídeos já salvos
  const [videosToRemove, setVideosToRemove] = useState([]); // Armazena URLs de vídeos a serem removidos

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [previewPhotos, setPreviewPhotos] = useState([]);
  const [photosToRemove, setPhotosToRemove] = useState([]); // URLs das fotos a remover
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ text: '', isError: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const { success, data } = await marketService.getById(id);

        if (success) {
          const videoLinks = data.videos?.filter(v => !v.includes('cloudinary')) || [];
          const uploadedVideos = data.videos?.filter(v => v.includes('cloudinary')) || [];

          setForm({
            name: data.name || '',
            age: data.age || '',
            city: data.city || '',
            phone: data.phone || '',
            position: data.position || '',
            experience: data.experience || '',
            description: data.description || '',
            videoLinks: videoLinks.join(', '), // Apenas links de texto aqui
            photos: [],
            existingPhotos: data.photos || []
          });

          setExistingVideos(uploadedVideos); // Seta os vídeos já upados
          setProfilePhotoPreview(data.profilePhoto);
          setPreviewPhotos(data.photos || []);
        } else {
          setSubmitMessage({ text: 'Erro ao carregar jogador', isError: true });
        }
      } catch (err) {
        console.error('Erro ao buscar jogador:', err);
        setSubmitMessage({ text: 'Erro ao conectar com o servidor', isError: true });
      } finally {
        setLoading(false);
      }
    };

    fetchPlayer();
  }, [id]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProfilePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length + form.existingPhotos.length - photosToRemove.length + form.photos.length > 5) {
      alert('Máximo de 5 fotos permitidas no total');
      return;
    }
    setForm(prev => ({ ...prev, photos: [...prev.photos, ...newFiles] }));
    const newPreviews = newFiles.map(file => URL.createObjectURL(file));
    setPreviewPhotos(prev => [...prev, ...newPreviews]);
  };

  // --- Nova função para lidar com upload de arquivos de vídeo ---
  const handleVideoUpload = (e) => {
    const newVideoFiles = Array.from(e.target.files);
    // Adicione um limite se desejar, por exemplo, 2 vídeos no total
    if (newVideoFiles.length + existingVideos.length - videosToRemove.length + videoFiles.length > 2) {
        alert('Máximo de 2 vídeos de upload permitido.');
        return;
    }
    setVideoFiles(prev => [...prev, ...newVideoFiles]);
  };

  const toggleRemovePhoto = (url) => {
    setPhotosToRemove(prev =>
      prev.includes(url) ? prev.filter(x => x !== url) : [...prev, url]
    );
  };
  
  // --- Nova função para marcar vídeos existentes para remoção ---
  const toggleRemoveVideo = (url) => {
    setVideosToRemove(prev =>
        prev.includes(url) ? prev.filter(x => x !== url) : [...prev, url]
    );
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ text: '', isError: false });

    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('age', form.age);
      formData.append('city', form.city.trim());
      formData.append('phone', form.phone.trim());
      formData.append('position', form.position.trim() || 'Não informado');
      formData.append('experience', form.experience.trim());
      formData.append('description', form.description.trim());
      formData.append('videoLinks', form.videoLinks.trim()); // Envia os links de texto
      
      if (profilePhoto) formData.append('profilePhoto', profilePhoto);

      // Envia os arquivos de imagem
      form.photos.forEach(file => formData.append('photos', file));
      photosToRemove.forEach(url => formData.append('photosToRemove', url));

      // --- Adiciona os arquivos de vídeo ao FormData ---
      videoFiles.forEach(file => formData.append('videos', file));
      videosToRemove.forEach(url => formData.append('videosToRemove', url));

      const { success, error } = await marketService.update(id, formData);

      if (success) {
        setSubmitMessage({ text: 'Jogador atualizado com sucesso!', isError: false });
        
        // --- AQUI ESTÁ A MUDANÇA ---
        // Em vez de ir para a página de detalhes, ele voltará para a página anterior.
        setTimeout(() => navigate(-1), 1500);

      } else {
        setSubmitMessage({ text: error || 'Erro ao atualizar jogador', isError: true });
      }
    } catch (err) {
      console.error('Erro ao atualizar jogador:', err);
      setSubmitMessage({ text: 'Erro ao conectar com o servidor', isError: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="market-loading">Carregando...</div>;
  if (!user) return <div className="market-error">Você precisa estar logado para editar</div>;

  return (
    <div className="market-form-container">
      <h2>✏️ Editar Jogador no Mercado</h2>

      {submitMessage.text && (
        <div className={`market-message ${submitMessage.isError ? 'error' : 'success'}`}>
          {submitMessage.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ... campos de nome, idade, cidade, telefone ... (sem alteração) */}
        <div className="market-form-grid">
          <div>
            <label>Nome completo*</label>
            <input name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div>
            <label>Idade*</label>
            <input name="age" type="number" min="14" max="60" value={form.age} onChange={handleChange} required />
          </div>
          <div>
            <label>Cidade*</label>
            <input name="city" value={form.city} onChange={handleChange} required />
          </div>
          <div>
            <label>Telefone*</label>
            <input name="phone" value={form.phone} onChange={handleChange} required />
          </div>
        </div>
        
        {/* ... campo de foto de perfil ... (sem alteração) */}
        <div>
          <label>Foto de Perfil</label>
          <input type="file" accept="image/*" onChange={handleProfilePhotoUpload}/>
          {profilePhotoPreview && (
            <div className="market-profile-photo-preview">
              <img src={profilePhotoPreview} alt="Preview da foto de perfil" style={{ maxWidth: '120px', marginTop: '10px' }}/>
            </div>
          )}
        </div>

        {/* ... campo de posição, experiência, descrição ... (sem alteração) */}
        <div>
          <label>Posição*</label>
          <select name="position" value={form.position} onChange={handleChange} required>
            <option value="">Selecione sua posição</option>
            <option value="Goleiro">Goleiro</option>
            <option value="Zagueiro">Zagueiro</option>
            <option value="Lateral">Lateral</option>
            <option value="Volante">Volante</option>
            <option value="Meia">Meia</option>
            <option value="Atacante">Atacante</option>
          </select>
        </div>
        <div>
          <label>Experiência</label>
          <textarea name="experience" placeholder="Times que já jogou, campeonatos, etc." value={form.experience} onChange={handleChange} rows="2"/>
        </div>
        <div>
          <label>Descrição</label>
          <textarea name="description" placeholder="Conte um pouco sobre seu estilo de jogo, qualidades, etc." value={form.description} onChange={handleChange} rows="3"/>
        </div>

        {/* ... campo de upload de fotos ... (pequeno ajuste na lógica de remoção) */}
        <div>
          <label>Fotos (máx. 5)</label>
          <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} />
          <div className="market-photo-preview">
            {previewPhotos.map((url, index) => (
              <div key={index} className="market-photo-preview-item">
                <img src={url} alt={`preview ${index}`} />
                <label className="remove-checkbox-label">
                  <input type="checkbox" checked={photosToRemove.includes(url)} onChange={() => toggleRemovePhoto(url)} />
                  Excluir
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* --- CAMPO DE UPLOAD DE VÍDEO (NOVO) --- */}
        <div>
          <label>Upload de Vídeos (máx. 2)</label>
          <input
            type="file"
            accept="video/mp4,video/mov,video/avi,video/webm"
            multiple
            onChange={handleVideoUpload}
          />
          {/* Preview de vídeos existentes */}
          {existingVideos.length > 0 && <p style={{marginTop: '10px', fontWeight: 'bold'}}>Vídeos atuais:</p>}
          <div className="market-video-preview">
            {existingVideos.map((url, index) => (
                <div key={index}>
                    <a href={url} target="_blank" rel="noopener noreferrer">Vídeo {index + 1}</a>
                    <label style={{marginLeft: '10px'}}>
                        <input type="checkbox" checked={videosToRemove.includes(url)} onChange={() => toggleRemoveVideo(url)} />
                        Excluir
                    </label>
                </div>
            ))}
          </div>
          {/* Preview de novos vídeos para upload */}
          {videoFiles.length > 0 && <p style={{marginTop: '10px', fontWeight: 'bold'}}>Novos vídeos para enviar:</p>}
          <div className="market-video-preview">
            {videoFiles.map((file, index) => (
                <p key={index}>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</p>
            ))}
          </div>
        </div>

        {/* --- CAMPO DE LINKS DE VÍDEO (MANTIDO) --- */}
        <div>
          <label>Links de Vídeos (YouTube, etc.)</label>
          <input
            name="videoLinks"
            placeholder="Cole URLs separadas por vírgula"
            value={form.videoLinks}
            onChange={handleChange}
          />
        </div>

        <div className="market-form-buttons">
          <button type="submit" disabled={isSubmitting} className="market-submit-button">
            {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
          </button>
          <button type="button" onClick={() => navigate('/market')} className="market-cancel-button">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default MarketEditForm;